import { useState, useEffect } from "react";
import {
  BatteryCharging,
  Clock3,
  Download,
  Image as ImageIcon,
  MonitorSmartphone,
  UploadCloud,
} from "lucide-react";
import { FiEdit2, FiCheck, FiX } from "react-icons/fi";
import { RiDragMove2Fill, RiDeleteBin6Line } from "react-icons/ri";
import { useConfig } from "../../context/configContext.js";
import { Toaster } from "react-hot-toast";
import toast from "react-hot-toast";
import api from "../interceptors.js"; // adjust path
import "../../App.css";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import SequenceEditor from "./SequenceEditor.js";

const ChargerUiUpdate = () => {
  const [mainData, setMainData] = useState({
    brandName: "",
    logoFile: {
      day: {},
      night: {},
    },
    guns: [],
    media: [],
    sequence: [],           // [{sequenceId, mediaId}]
    playlistId: null,
    houseMedia: [],
    houseSequence: [],      // [{sequenceId, mediaId}]
    housePlaylistId: null,
    campaigns: [],          // [{campaign_id, campaign_name, advertiser_id, advertiser_name}]
    lastUpdate: "",
  });

  const [logoProgress, setLogoProgress] = useState({ day: 0, night: 0 });
  const [logoUploadStatus, setLogoUploadStatus] = useState({
    day: "",
    night: "",
  });
  const [isEditing, setIsEditing] = useState(false);
  const [showMediaOptions, setShowMediaOptions] = useState(false);

  const { config } = useConfig();
  const station_id = config.station_id;
  const isMobile = window.innerWidth < 768;
  const totalDispensers = config.dispensersData?.length || 0;
  const totalGuns = (config.dispensersData || []).reduce(
    (total, dispenser) => total + (dispenser.guns?.length || 0),
    0,
  );

  const handleStopChange = (gun_id, dispensor_id, value) => {
    setMainData((prev) => ({
      ...prev,
      guns: prev.guns.map((g) =>
        g.gun_id === gun_id && g.dispensor_id === dispensor_id
          ? { ...g, stopBtn: value }
          : g,
      ),
    }));
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(mainData.media);
    const [reordered] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reordered);

    const updated = items.map((item, index) => ({
      ...item,
      priority: index + 1,
    }));

    setMainData((prev) => ({
      ...prev,
      media: updated,
    }));
  };

  const handleDownload = async (type) => {
    try {
      const logoUrl = mainData.logo?.[type]?.link;
      if (!logoUrl) return;

      const response = await fetch(logoUrl);
      if (!response.ok) throw new Error("Failed to fetch image");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const brand = mainData.brandName
        ? mainData.brandName.replace(/\s+/g, "_").toLowerCase()
        : "brand";

      const link = document.createElement("a");
      link.href = url;
      link.download = `${brand}_${type}.png`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download error:", error);
    }
  };

  const handleFileUpload = (event, index, type) => {
    const file = event.target.files[0];
    if (!file) return;

    setMainData((prev) => ({
      ...prev,
      media: prev.media.map((v, i) =>
        i === index
          ? {
              ...v,
              progress: 0,
              uploadStatus: "uploading",
              uploadMessage: "Preparing upload...",
            }
          : v,
      ),
    }));

    const formData = new FormData();
    formData.append("file", file);
    formData.append("type", type);

    const xhr = new XMLHttpRequest();

    xhr.open("POST", "/api/upload-media");
    const token = localStorage.getItem("access_token");
    if (token) {
      xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    }

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        const percent = Math.min(90, Math.round((e.loaded * 90) / e.total));

        setMainData((prev) => ({
          ...prev,
          media: prev.media.map((v, i) =>
            i === index
              ? {
                  ...v,
                  progress: percent,
                  uploadStatus: "uploading",
                  uploadMessage: `Uploading to server... ${percent}%`,
                }
              : v,
          ),
        }));
      }
    };

    xhr.upload.onload = () => {
      setMainData((prev) => ({
        ...prev,
        media: prev.media.map((v, i) =>
          i === index
            ? {
                ...v,
                progress: 95,
                uploadStatus: "processing",
                uploadMessage: "Uploading to Cloudinary...",
              }
            : v,
        ),
      }));
    };

    xhr.onload = () => {
      let data = {};
      try {
        data = JSON.parse(xhr.responseText || "{}");
      } catch {
        data = { error: xhr.responseText || "Upload failed" };
      }

      if (xhr.status < 200 || xhr.status >= 300) {
        const message = data.error || data.message || "Upload failed";
        toast.error(message);
        setMainData((prev) => ({
          ...prev,
          media: prev.media.map((v, i) =>
            i === index
              ? {
                  ...v,
                  progress: 0,
                  uploadStatus: "error",
                  uploadMessage: message,
                }
              : v,
          ),
        }));
        event.target.value = "";
        return;
      }

      setMainData((prev) => ({
        ...prev,
        media: prev.media.map((v, i) =>
          i === index
            ? {
                ...v,
                fileName: file.name,
                link: data.url,
                size: file.size,
                aspectRatio: data.aspect_ratio || v.aspectRatio || "",
                checksum: data.checksum || v.checksum || "",
                progress: 100,
                uploadStatus: "success",
                uploadMessage: "Uploaded to Cloudinary",
              }
            : v,
        ),
      }));
      toast.success("Media uploaded");
      event.target.value = "";
    };

    xhr.onerror = () => {
      toast.error("Network error while uploading media");
      setMainData((prev) => ({
        ...prev,
        media: prev.media.map((v, i) =>
          i === index
            ? {
                ...v,
                progress: 0,
                uploadStatus: "error",
                uploadMessage: "Network error while uploading media",
              }
            : v,
        ),
      }));
      event.target.value = "";
    };

    xhr.send(formData);
  };

  const handleHouseMediaUpload = (event, index, type) => {
    const file = event.target.files[0];
    if (!file) return;

    const updateHouseMedia = (updater) => {
      setMainData((prev) => ({
        ...prev,
        houseMedia: prev.houseMedia.map((v, i) =>
          i === index ? updater(v) : v,
        ),
      }));
    };

    updateHouseMedia((v) => ({
      ...v,
      progress: 0,
      uploadStatus: "uploading",
      uploadMessage: "Preparing upload...",
    }));

    const formData = new FormData();
    formData.append("file", file);
    formData.append("type", type);

    const xhr = new XMLHttpRequest();

    xhr.open("POST", "/api/upload-media");
    const token = localStorage.getItem("access_token");
    if (token) {
      xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    }

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        const percent = Math.min(90, Math.round((e.loaded * 90) / e.total));
        updateHouseMedia((v) => ({
          ...v,
          progress: percent,
          uploadStatus: "uploading",
          uploadMessage: `Uploading to server... ${percent}%`,
        }));
      }
    };

    xhr.upload.onload = () => {
      updateHouseMedia((v) => ({
        ...v,
        progress: 95,
        uploadStatus: "processing",
        uploadMessage: "Uploading to Cloudinary...",
      }));
    };

    xhr.onload = () => {
      let data = {};
      try {
        data = JSON.parse(xhr.responseText || "{}");
      } catch {
        data = { error: xhr.responseText || "Upload failed" };
      }

      if (xhr.status < 200 || xhr.status >= 300) {
        const message = data.error || data.message || "Upload failed";
        toast.error(message);
        updateHouseMedia((v) => ({
          ...v,
          progress: 0,
          uploadStatus: "error",
          uploadMessage: message,
        }));
        event.target.value = "";
        return;
      }

      updateHouseMedia((v) => ({
        ...v,
        fileName: file.name,
        link: data.url,
        size: file.size,
        aspectRatio: data.aspect_ratio || v.aspectRatio || "",
        checksum: data.checksum || "",
        progress: 100,
        uploadStatus: "success",
        uploadMessage: "Uploaded to Cloudinary",
      }));
      toast.success("House media uploaded");
      event.target.value = "";
    };

    xhr.onerror = () => {
      toast.error("Network error while uploading media");
      updateHouseMedia((v) => ({
        ...v,
        progress: 0,
        uploadStatus: "error",
        uploadMessage: "Network error while uploading media",
      }));
      event.target.value = "";
    };

    xhr.send(formData);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api.get(`/api/charger-ui/${station_id}`);
        const data = res.data;

        if (!data) return;

        // Logo mapping
        const logo = {
          day: data.logoFile?.[0] || {},
          night: data.logoFile?.[1] || {},
        };

        // Flatten guns
        const guns = (data.dispensers || []).flatMap((d) =>
          d.guns.map((g) => ({
            dispensor_id: d.dispensor_id,
            gun_id: g.gun_id,
            id: Number(g.gun_id.replace("GUN", "")),
            stopBtn: g.stopBtn ?? false,
            pendingScreenCarMode: g.pendingScreenCarMode || "",
          })),
        );

        // Convert UTC expiry ISO strings to datetime-local format in browser's local tz
        const utcToLocalDatetimeLocal = (utcStr) => {
          if (!utcStr) return "";
          const d = new Date(utcStr);
          if (isNaN(d.getTime())) return "";
          const pad = (n) => String(n).padStart(2, "0");
          return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
        };

        // Media with campaign/advertiser info
        const media = (data.media || []).map((m) => ({
          ...m,
          expiry: utcToLocalDatetimeLocal(m.expiry),
        }));

        // Sequence
        const sequence = data.sequence || [];

        // House media
        const houseMedia = (data.houseMedia || []).map((m) => ({
          ...m,
          expiry: utcToLocalDatetimeLocal(m.expiry),
        }));

        // Fetch campaigns assigned to this station
        let campaigns = [];
        try {
          const campRes = await api.get(`/api/campaigns/station/${station_id}`);
          campaigns = campRes.data?.campaigns || [];
        } catch (err) {
          console.warn("Failed to fetch campaigns:", err);
        }

        setMainData({
          brandName: data.brandName || "",
          logo,
          guns,
          media,
          sequence,
          playlistId: data.playlistId || null,
          houseMedia,
          houseSequence: data.houseSequence || [],
          housePlaylistId: data.housePlaylistId || null,
          campaigns,
          lastUpdate: data.lastUpdate || "",
        });
      } catch (err) {
        console.error(err);
        toast.error("Failed to load data");
      }
    };

    fetchData();
  }, [station_id]);


  const handleCancel = () => {
    setIsEditing(false);
  };

  const deleteMedia = (index) => {
    setMainData((prev) => {
      const updated = prev.media.filter((_, i) => i !== index);
      const reordered = updated.map((item, i) => ({ ...item, priority: i + 1 }));
      return { ...prev, media: reordered };
    });
  };

  // House media helpers (replaces old addDefaultMedia, deleteDefaultMedia, updateDefaultMediaField)
  const addHouseMedia = (type) => {
    if (!isEditing) return;
    setMainData((prev) => ({
      ...prev,
      houseMedia: [
        ...(prev.houseMedia || []),
        {
          mediaId: null,
          fileName: "",
          link: "",
          type,
          size: "",
          duration: type === "video" ? undefined : "",
          aspectRatio: "",
          expiry: "",
          campaignId: "",
          campaignName: "",
          advertiserId: "",
          advertiserName: "",
          checksum: "",
        },
      ],
    }));
  };

  const deleteHouseMedia = (index) => {
    setMainData((prev) => ({
      ...prev,
      houseMedia: prev.houseMedia.filter((_, i) => i !== index),
    }));
  };

  const updateHouseMediaField = (index, field, value) => {
    setMainData((prev) => ({
      ...prev,
      houseMedia: prev.houseMedia.map((v, i) =>
        i === index ? { ...v, [field]: value } : v,
      ),
    }));
  };

  useEffect(() => {
    if (!config.dispensersData) return;

    const dispenserGuns = (config.dispensersData || []).flatMap((d) =>
      d.guns.map((g) => ({
        dispensor_id: d.dispensor_id,
        id: Number(g.gun_id.replace("GUN", "")),
        gun_id: g.gun_id,
        power: g.power,
        stopBtn: false,
        pendingScreenCarMode: "",
      })),
    );

    setMainData((prev) => ({
      ...prev,
      guns: dispenserGuns,
    }));
  }, [config.dispensersData]);

  const handlePendingChange = (gun_id, dispensor_id, value) => {
    setMainData((prev) => ({
      ...prev,
      guns: prev.guns.map((g) =>
        g.gun_id === gun_id && g.dispensor_id === dispensor_id
          ? { ...g, pendingScreenCarMode: value }
          : g,
      ),
    }));
  };

  const addMedia = (type) => {
    if (!isEditing) return;

    setMainData((prev) => ({
      ...prev,
      media: [
        ...(prev.media || []),
        {
          mediaId: null,
          fileName: "",
          link: "",
          type,
          size: "",
          duration: type === "video" ? undefined : "",
          aspectRatio: "",
          expiry: "",
          campaignId: "",
          campaignName: "",
          advertiserId: "",
          advertiserName: "",
          checksum: "",
        },
      ],
    }));
    setShowMediaOptions(false);
  };

  const handleSubmit = async () => {
    // Convert a datetime-local string to a full ISO 8601 string with the browser's
    // current timezone offset (e.g. "2026-06-10T14:30" -> "2026-06-10T14:30:00+05:30").
    // The backend will parse this and convert to UTC for storage.
    const localDatetimeToIso = (value) => {
      if (!value) return null;
      const date = new Date(value);
      if (isNaN(date.getTime())) return null;
      const offset = -date.getTimezoneOffset(); // minutes, positive = east
      const sign = offset >= 0 ? "+" : "-";
      const absOffset = Math.abs(offset);
      const hh = String(Math.floor(absOffset / 60)).padStart(2, "0");
      const mm = String(absOffset % 60).padStart(2, "0");
      // datetime-local value is "YYYY-MM-DDTHH:MM" — append seconds + tz
      return `${value}:00${sign}${hh}:${mm}`;
    };

    const mapMediaForPayload = (m) => ({
      mediaId: m.mediaId || null,
      fileName: m.fileName,
      link: m.link,
      size: m.size,
      type: m.type,
      duration: m.duration,
      aspectRatio: m.aspectRatio || null,
      expiry: localDatetimeToIso(m.expiry),
      campaignId: m.campaignId || null,
      checksum: m.checksum || null,
    });

    try {
      const payload = {
        station_id,
        brandName: mainData.brandName,
        dispensers: config.dispensersData.map((d) => ({
          dispensor_id: d.dispensor_id,
          guns: d.guns.map((g) => {
            const gunData = mainData.guns.find(
              (x) => x.gun_id === g.gun_id && x.dispensor_id === d.dispensor_id,
            );
            return {
              gun_id: g.gun_id,
              id: Number(g.gun_id.replace("GUN", "")),
              stopBtn: gunData?.stopBtn || false,
              QrString: gunData?.pendingScreenCarMode || "",
            };
          }),
        })),
        logoFile: [mainData.logo.day, mainData.logo.night],
        // Regular playlist
        playlistId: mainData.playlistId,
        media: mainData.media.map(mapMediaForPayload),
        sequence: (mainData.sequence || []).map((s) => ({
          sequenceId: s.sequenceId,
          mediaId: s.mediaId,
        })),
        // House playlist
        housePlaylistId: mainData.housePlaylistId,
        houseMedia: (mainData.houseMedia || []).map(mapMediaForPayload),
        houseSequence: (mainData.houseSequence || []).map((s) => ({
          sequenceId: s.sequenceId,
          mediaId: s.mediaId,
        })),
      };

      await api.post("/api/charger-ui", payload);

      toast.success("Saved successfully");
      setIsEditing(false);
    } catch (err) {
      console.error(err);
      toast.error("Save failed");
    }
  };

  const handleLogoChange = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    const fileNameWithoutExt = file.name.split(".")[0];

    const publicId =
      type === "day"
        ? `${fileNameWithoutExt}_day`
        : `${fileNameWithoutExt}_night`;

    const formData = new FormData();
    formData.append("file", file);
    formData.append("type", "image");
    formData.append("public_id", publicId);

    try {
      setLogoProgress((prev) => ({
        ...prev,
        [type]: 0,
      }));
      setLogoUploadStatus((prev) => ({
        ...prev,
        [type]: "Preparing upload...",
      }));

      const res = await api.post("/api/upload-media", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },

        onUploadProgress: (e) => {
          const percent = e.total
            ? Math.min(90, Math.round((e.loaded * 90) / e.total))
            : 90;

          setLogoProgress((prev) => ({
            ...prev,
            [type]: percent,
          }));
          setLogoUploadStatus((prev) => ({
            ...prev,
            [type]:
              percent >= 90
                ? "Uploading to Cloudinary..."
                : `Uploading to server... ${percent}%`,
          }));
        },
      });

      const data = res.data;

      setMainData((prev) => ({
        ...prev,
        logo: {
          ...prev.logo,
          [type]: {
            fileName: file.name,
            link: data.url || data.link,
            size: file.size,
          },
        },
      }));
      setLogoProgress((prev) => ({
        ...prev,
        [type]: 100,
      }));
      setLogoUploadStatus((prev) => ({
        ...prev,
        [type]: "Uploaded to Cloudinary",
      }));
      toast.success(`${type === "day" ? "Day" : "Night"} logo uploaded`);
    } catch (err) {
      console.error("Logo upload failed", err);
      const message =
        err.response?.data?.error ||
        err.response?.data?.message ||
        "Logo upload failed";

      toast.error(message);
      setLogoProgress((prev) => ({
        ...prev,
        [type]: 0,
      }));
      setLogoUploadStatus((prev) => ({
        ...prev,
        [type]: message,
      }));
    }
  };

  return (
    <div className="mx-auto w-full max-w-full overflow-x-hidden text-slate-50">
      {/* Brand */}
      <Toaster position={isMobile ? "bottom-center" : "top-right"} />{" "}
      <div className="mb-6 grid min-w-0 max-w-full gap-5 rounded-lg border border-cyan-100/40 bg-white/95 p-4 text-slate-900 shadow-2xl shadow-blue-950/20 backdrop-blur sm:p-5 xl:grid-cols-[minmax(0,1fr)_auto] xl:p-6">
        {" "}
        {/* Left side (logo + brand) */}
        <div className="grid min-w-0 gap-5 lg:grid-cols-[auto_minmax(0,1fr)] lg:items-center">
          {" "}
          <div className="grid grid-cols-2 gap-3 sm:gap-4">
            {/* Day Logo */}
            <div className="rounded-lg border border-sky-100 bg-sky-50/70 p-3">
              <p className="mb-2 text-xs font-black uppercase text-slate-500">DAY</p>

              <div className="relative flex aspect-square w-full min-w-0 max-w-32 items-center justify-center rounded-lg border border-sky-100 bg-white shadow-inner">
                <img
                  src={mainData.logo?.day?.link}
                  className="max-w-[80%] max-h-[80%] object-contain"
                  alt="day logo"
                />

                {isEditing && (
                  <label className="absolute bottom-2 right-2 cursor-pointer rounded-lg bg-sky-600 p-2 shadow-lg">
                    <FiEdit2 className="text-white text-sm" />

                    <input
                      type="file"
                      accept="image/png"
                      onChange={(e) => handleLogoChange(e, "day")}
                      className="hidden"
                    />
                  </label>
                )}
              </div>
            </div>

            {/* Night Logo */}
            <div className="rounded-lg border border-slate-200 bg-slate-100 p-3">
              <p className="mb-2 text-xs font-black uppercase text-slate-500">NIGHT</p>

              <div className="relative flex aspect-square w-full min-w-0 max-w-32 items-center justify-center rounded-lg border border-slate-700 bg-slate-950 shadow-inner">
                <img
                  src={mainData.logo?.night?.link}
                  className="max-w-[80%] max-h-[80%] object-contain text-white"
                  alt="night logo"
                />

                {isEditing && (
                  <label className="absolute bottom-2 right-2 cursor-pointer rounded-lg bg-sky-600 p-2 shadow-lg">
                    <FiEdit2 className="text-white text-sm" />

                    <input
                      type="file"
                      accept="image/png"
                      onChange={(e) => handleLogoChange(e, "night")}
                      className="hidden"
                    />
                  </label>
                )}
              </div>
            </div>
          </div>
          {/* <div className="relative w-20 h-20">
           
          </div> */}
          {logoUploadStatus.day && (
            <div className="col-span-2 rounded-lg border border-sky-100 bg-sky-50 p-3">
              <div className="mb-2 flex items-center justify-between gap-3">
                <span
                  className={`text-xs font-black uppercase ${
                    logoProgress.day === 0
                      ? "text-red-600"
                      : logoProgress.day === 100
                        ? "text-emerald-700"
                        : "text-sky-700"
                  }`}
                >
                  {logoUploadStatus.day || "Uploading day logo..."}
                </span>
                <span className="text-xs font-black text-slate-700">
                  {logoProgress.day}%
                </span>
              </div>
              <div className="h-2 overflow-hidden rounded bg-gray-200">
                <div
                  className={`h-2 rounded transition-all ${
                    logoProgress.day === 0
                      ? "bg-red-500"
                      : logoProgress.day === 100
                        ? "bg-emerald-500"
                        : "bg-cyan-400"
                  }`}
                  style={{ width: `${logoProgress.day}%` }}
                />
              </div>
            </div>
          )}
          {logoUploadStatus.night && (
            <div className="col-span-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
              <div className="mb-2 flex items-center justify-between gap-3">
                <span
                  className={`text-xs font-black uppercase ${
                    logoProgress.night === 0
                      ? "text-red-600"
                      : logoProgress.night === 100
                        ? "text-emerald-700"
                        : "text-slate-700"
                  }`}
                >
                  {logoUploadStatus.night || "Uploading night logo..."}
                </span>
                <span className="text-xs font-black text-slate-700">
                  {logoProgress.night}%
                </span>
              </div>
              <div className="h-2 overflow-hidden rounded bg-gray-200">
                <div
                  className={`h-2 rounded transition-all ${
                    logoProgress.night === 0
                      ? "bg-red-500"
                      : logoProgress.night === 100
                        ? "bg-emerald-500"
                        : "bg-cyan-400"
                  }`}
                  style={{ width: `${logoProgress.night}%` }}
                />
              </div>
            </div>
          )}
          <div className="min-w-0 flex-1">
            <p className="mb-1 text-xs font-black uppercase text-sky-700">
              Brand package
            </p>
            <input
              type="text"
              value={mainData.brandName}
              disabled={!isEditing}
              onChange={(e) =>
                setMainData({
                  ...mainData,
                  brandName: e.target.value,
                })
              }
              className="w-full min-w-0 border-b border-sky-200 bg-transparent pb-1 text-2xl font-black text-slate-900 outline-none focus:border-cyan-400 md:text-3xl"
            />

            <p className="mt-2 text-sm font-semibold text-slate-600">
              Station ID:{" "}
              <span className="font-medium text-slate-800">{station_id}</span>
            </p>

            <div className="mt-4 grid gap-2 sm:grid-cols-3">
              <span className="inline-flex items-center gap-2 rounded-lg border border-sky-100 bg-sky-50 px-3 py-2 text-sm font-black text-sky-900">
                <MonitorSmartphone size={16} />
                {totalDispensers} Dispensers
              </span>
              <span className="inline-flex items-center gap-2 rounded-lg border border-sky-100 bg-sky-50 px-3 py-2 text-sm font-black text-sky-900">
                <BatteryCharging size={16} />
                {totalGuns} Guns
              </span>
              <span className="inline-flex items-center gap-2 rounded-lg border border-sky-100 bg-sky-50 px-3 py-2 text-sm font-black text-sky-900">
                <ImageIcon size={16} />
                {mainData.media?.length || 0} Media
              </span>
            </div>

            <div className="mt-4 flex flex-col gap-2 sm:flex-row">
              <button
                onClick={() => handleDownload("day")}
                className="inline-flex min-h-9 items-center justify-center gap-2 rounded-lg border border-sky-100 px-3 py-2 text-sm font-black text-sky-700 hover:bg-sky-50"
              >
                <Download size={15} />
                Day PNG
              </button>

              <button
                onClick={() => handleDownload("night")}
                className="inline-flex min-h-9 items-center justify-center gap-2 rounded-lg border border-sky-100 px-3 py-2 text-sm font-black text-sky-700 hover:bg-sky-50"
              >
                <Download size={15} />
                Night PNG
              </button>
            </div>
          </div>
        </div>
        {/* Right side buttons */}
        <div className="flex flex-wrap items-center gap-3 xl:justify-end xl:self-start">
          {!isEditing && (
            <button
              onClick={() => setIsEditing(true)}
              className="flex min-h-10 items-center gap-2 rounded-lg bg-sky-600 px-4 py-2 font-black text-white shadow transition hover:bg-sky-700"
            >
              <FiEdit2 />
              Edit
            </button>
          )}

          {isEditing && (
            <>
              <button
                onClick={handleSubmit}
                className="flex min-h-10 items-center gap-2 rounded-lg bg-sky-600 px-4 py-2 font-black text-white shadow transition hover:bg-sky-700"
              >
                <FiCheck />
                Submit
              </button>

              <button
                onClick={handleCancel}
                className="flex min-h-10 items-center gap-2 rounded-lg bg-white px-4 py-2 font-black text-red-600 shadow ring-1 ring-red-200 transition hover:bg-red-50"
              >
                <FiX />
                Cancel
              </button>
            </>
          )}
        </div>
      </div>
      {/* Guns */}
      <div className="rounded-lg border border-cyan-100/25 bg-slate-950/20 p-4 shadow-xl shadow-blue-950/10 backdrop-blur md:p-5">
        <div className="mb-4 flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-xs font-black uppercase text-cyan-100/80">
              Gun behavior
            </p>
            <h2 className="m-0 text-xl font-black text-white">
              Dispenser Controls
            </h2>
          </div>
          <span className="text-sm font-bold text-cyan-50/80">
            {totalGuns} guns configured
          </span>
        </div>

        {[...(config.dispensersData || [])]
          .sort((a, b) => a.dispensor_id.localeCompare(b.dispensor_id))
          .map((disp) => (
            <div key={disp.dispensor_id} className="mb-6 last:mb-0">
              <h2 className="mb-3 text-lg font-black text-white">
                Dispensor {disp.dispensor_id}
              </h2>

              <div className="grid gap-4 lg:grid-cols-2 2xl:grid-cols-3">
                {disp.guns.map((gun) => {
                  const gunsMap = Object.fromEntries(
                    mainData.guns.map((g) => [
                      `${g.dispensor_id}-${g.gun_id}`,
                      g,
                    ]),
                  );

                  const gunData =
                    gunsMap[`${disp.dispensor_id}-${gun.gun_id}`] || {};

                  return (
                    <div
                      key={gun.gun_id}
                      className="rounded-lg border border-cyan-100/70 bg-white/95 p-4 text-slate-900 shadow-sm shadow-blue-950/10 transition hover:-translate-y-0.5 hover:shadow-lg sm:p-5"
                    >
                      <div className="mb-4 flex items-center justify-between gap-3">
                        <span className="inline-flex min-h-9 items-center rounded-lg bg-sky-50 px-3 text-sm font-black text-sky-900">
                          {gun.gun_id}
                        </span>
                        {gun.power && (
                          <span className="text-xs font-black uppercase text-slate-500">
                            {gun.power}
                          </span>
                        )}
                      </div>

                      {/* Stop Button */}
                      <div className="mb-4">
                        <p className="text-sm font-semibold mb-2">
                          Stop Button Required
                        </p>

                        <div className="mt-2 grid grid-cols-2 gap-2">
                          <label className="flex min-h-10 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-slate-50 text-sm font-bold">
                            <input
                              type="radio"
                              name={`stop-${disp.dispensor_id}-${gun.gun_id}`}
                              checked={gunData.stopBtn === true}
                              disabled={!isEditing}
                              className={`${isEditing ? "accent-cyan-500" : "accent-gray-400"}`}
                              onChange={() =>
                                handleStopChange(
                                  gun.gun_id,
                                  disp.dispensor_id,
                                  true,
                                )
                              }
                            />
                            Yes
                          </label>

                          <label className="flex min-h-10 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-slate-50 text-sm font-bold">
                            <input
                              type="radio"
                              name={`stop-${disp.dispensor_id}-${gun.gun_id}`}
                              checked={gunData.stopBtn === false}
                              className={`${isEditing ? "accent-cyan-500" : "accent-gray-400"}`}
                              disabled={!isEditing}
                              onChange={() =>
                                handleStopChange(
                                  gun.gun_id,
                                  disp.dispensor_id,
                                  false,
                                )
                              }
                            />
                            No
                          </label>
                        </div>
                      </div>

                      {/* Pending Screen */}
                      <input
                        type="text"
                        value={gunData.pendingScreenCarMode || ""}
                        disabled={!isEditing}
                        placeholder="Qr String"
                        onChange={(e) =>
                          handlePendingChange(
                            gun.gun_id,
                            disp.dispensor_id,
                            e.target.value,
                          )
                        }
                        className={`min-h-11 w-full rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-sm font-semibold text-slate-900 outline-none focus:border-sky-400 focus:ring-4 focus:ring-sky-500/15 ${!isEditing && "cursor-not-allowed bg-slate-100"}`}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
      </div>
      {/* House Ads */}
      <div className="my-6 rounded-lg border border-cyan-100/25 bg-slate-950/20 p-4 shadow-xl shadow-blue-950/10 backdrop-blur md:p-5">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-xs font-black uppercase text-cyan-100/80">Fallback Playlist</p>
            <h2 className="m-0 text-xl font-black text-white">House Ads</h2>
            <p className="mt-1 text-xs text-cyan-100/50">Shown on this charger when no paid media is scheduled</p>
          </div>
          {isEditing && (
            <div className="flex gap-2">
              {["image", "video", "gif"].map((t) => (
                <button
                  key={t}
                  onClick={() => addHouseMedia(t)}
                  className="min-h-10 rounded-lg bg-white px-3 py-2 text-sm font-black text-sky-950 hover:bg-cyan-50"
                >
                  + {t[0].toUpperCase() + t.slice(1)}
                </button>
              ))}
            </div>
          )}
        </div>
        {!mainData.houseMedia?.length && (
          <p className="text-sm text-cyan-100/80">No house ads added yet</p>
        )}
        {(mainData.houseMedia || []).map((dm, dmIdx) => (
          <div key={dmIdx} className="mb-3 flex flex-wrap items-center gap-3 rounded-lg border border-cyan-100/30 bg-white/10 px-4 py-3">
            <span className="shrink-0 rounded-full bg-sky-900/80 px-2 py-0.5 text-xs font-black text-sky-200">{dm.type}</span>
            <span className="shrink-0 text-xs font-black text-cyan-100/50">#{dmIdx + 1}</span>
            <input
              placeholder="File name"
              value={dm.fileName || ""}
              disabled={!isEditing}
              onChange={(e) => updateHouseMediaField(dmIdx, "fileName", e.target.value)}
              className="min-h-9 min-w-0 flex-1 rounded-lg border border-white/20 bg-white/10 px-3 py-1.5 text-sm font-semibold text-white placeholder-white/40 outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-500/15"
            />
            <input
              placeholder="File URL"
              value={dm.link || ""}
              disabled={!isEditing}
              onChange={(e) => updateHouseMediaField(dmIdx, "link", e.target.value)}
              className="min-h-9 min-w-0 flex-[2] rounded-lg border border-white/20 bg-white/10 px-3 py-1.5 text-sm font-semibold text-white placeholder-white/40 outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-500/15"
            />
            {(dm.type === "image" || dm.type === "gif") && (
              <input
                type="number"
                placeholder="Duration (s)"
                value={dm.duration || ""}
                disabled={!isEditing}
                onChange={(e) => updateHouseMediaField(dmIdx, "duration", e.target.value)}
                className="min-h-9 w-28 rounded-lg border border-white/20 bg-white/10 px-3 py-1.5 text-sm font-semibold text-white placeholder-white/40 outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-500/15"
              />
            )}
            <input
              placeholder="Ratio"
              value={dm.aspectRatio || ""}
              disabled={!isEditing}
              onChange={(e) => updateHouseMediaField(dmIdx, "aspectRatio", e.target.value)}
              className="min-h-9 w-24 rounded-lg border border-white/20 bg-white/10 px-3 py-1.5 text-sm font-semibold text-white placeholder-white/40 outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-500/15"
            />
            {/* Campaign selector for house media */}
            <select
              value={dm.campaignId || ""}
              disabled={!isEditing}
              onChange={(e) => {
                const camp = mainData.campaigns.find(
                  (c) => c.campaign_id === e.target.value,
                );
                setMainData((prev) => ({
                  ...prev,
                  houseMedia: prev.houseMedia.map((v, i) =>
                    i === dmIdx
                      ? {
                          ...v,
                          campaignId: camp?.campaign_id || "",
                          campaignName: camp?.campaign_name || "",
                          advertiserId: camp?.advertiser_id || "",
                          advertiserName: camp?.advertiser_name || "",
                        }
                      : v,
                  ),
                }));
              }}
              className="min-h-9 min-w-0 flex-1 rounded-lg border border-white/20 bg-white/10 px-2 py-1.5 text-sm font-semibold text-white outline-none focus:border-sky-400"
            >
              <option value="" className="bg-slate-800">— Campaign —</option>
              {(mainData.campaigns || []).map((c) => (
                <option key={c.campaign_id} value={c.campaign_id} className="bg-slate-800">
                  {c.campaign_name} ({c.advertiser_name})
                </option>
              ))}
            </select>
            {dm.advertiserName && (
              <span className="shrink-0 rounded-full bg-amber-900/50 px-2 py-0.5 text-[10px] font-black text-amber-200">
                {dm.advertiserName}
              </span>
            )}
            {isEditing && (
              <label className="shrink-0 cursor-pointer rounded-lg bg-cyan-600 px-3 py-1.5 text-sm font-black text-white hover:bg-cyan-700">
                <UploadCloud size={16} className="inline mr-1" />
                Upload
                <input
                  type="file"
                  hidden
                  disabled={!isEditing}
                  accept={dm.type === "video" ? "video/*" : dm.type === "gif" ? "image/gif" : "image/png,image/jpeg"}
                  onChange={(e) => handleHouseMediaUpload(e, dmIdx, dm.type)}
                />
              </label>
            )}
            {isEditing && (
              <button onClick={() => deleteHouseMedia(dmIdx)} className="shrink-0 text-red-400 hover:text-red-200 transition">
                <RiDeleteBin6Line size={16} />
              </button>
            )}
            {(dm.uploadStatus || dm.progress > 0) && (
              <div className="w-full mt-2 flex items-center justify-between gap-3 rounded-lg bg-white/5 p-2">
                <span className={`text-xs font-black uppercase ${dm.uploadStatus === "error" ? "text-red-400" : dm.uploadStatus === "success" ? "text-emerald-400" : "text-sky-300"}`}>
                  {dm.uploadStatus === "uploading" ? "Uploading" : dm.uploadStatus === "processing" ? "Processing" : dm.uploadStatus === "success" ? "Success" : "Error"}
                </span>
                <div className="flex-1">
                  <div className="h-2 overflow-hidden rounded-full bg-white/10">
                    <div className={`h-full rounded-full transition-all duration-300 ${dm.uploadStatus === "error" ? "bg-red-500" : dm.uploadStatus === "success" ? "bg-emerald-500" : "bg-sky-400"}`} style={{ width: `${dm.progress || 0}%` }} />
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      {/* House Ads Sequence */}
      <SequenceEditor
        sequence={mainData.houseSequence}
        media={mainData.houseMedia}
        isEditing={isEditing}
        onSequenceChange={(newSeq) =>
          setMainData((prev) => ({ ...prev, houseSequence: newSeq }))
        }
        title="House Ads Sequence"
      />
      <div className="my-6 rounded-lg border border-cyan-100/25 bg-slate-950/20 p-4 shadow-xl shadow-blue-950/10 backdrop-blur md:p-5">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-xs font-black uppercase text-cyan-100/80">
              Playlist
            </p>
            <h2 className="m-0 text-xl font-black text-white">
              Media Schedule
            </h2>
          </div>

          <div className="relative">
            <button
              onClick={() => setShowMediaOptions(!showMediaOptions)}
              disabled={!isEditing}
              className={`min-h-10 rounded-lg px-4 py-2 text-sm font-black ${
                isEditing
                  ? "bg-white text-sky-950 hover:bg-cyan-50"
                  : "bg-white/30 text-cyan-100 cursor-not-allowed"
              }`}
            >
              + Add Media
            </button>

            {showMediaOptions && isEditing && (
              <div className="absolute right-0 z-10 mt-2 w-40 overflow-hidden rounded-lg border border-sky-100 bg-white text-slate-900 shadow-lg">
                <button
                  onClick={() => addMedia("image")}
                  className="block w-full text-left px-4 py-2 hover:bg-gray-100 text-sm"
                >
                  Image
                </button>

                <button
                  onClick={() => addMedia("video")}
                  className="block w-full text-left px-4 py-2 hover:bg-gray-100 text-sm"
                >
                  Video
                </button>

                <button
                  onClick={() => addMedia("gif")}
                  className="block w-full text-left px-4 py-2 hover:bg-gray-100 text-sm"
                >
                  GIF
                </button>
              </div>
            )}
          </div>
        </div>

        {!mainData.media?.length && (
          <p className="text-sm text-cyan-100/80">No media added yet</p>
        )}

        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="mediaList">
            {(provided) => (
              <div ref={provided.innerRef} {...provided.droppableProps}>
                {(mainData.media || []).map((m, index) => (
                  <Draggable
                    key={`${index}-${m.fileName}`}
                    draggableId={`${index}-${m.fileName}`}
                    index={index}
                  >
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        className="mb-3 rounded-lg border border-cyan-100/70 bg-white/95 p-5 text-slate-900 shadow-sm shadow-blue-950/10 transition hover:shadow-lg"
                      >
                        {/* HEADER */}
                        <div className="flex justify-between items-center mb-3">
                          <div
                            {...provided.dragHandleProps}
                            className="flex min-w-0 items-center gap-2 text-slate-500"
                          >
                            <RiDragMove2Fill className="cursor-grab" />
                            <span className="rounded-full bg-sky-50 px-2 py-1 text-xs font-black uppercase text-sky-800">
                              {m.type}
                            </span>
                            {/* <span className="truncate text-sm font-black text-slate-800">
                              Priority {m.priority || index + 1}
                            </span> */}
                          </div>

                          {isEditing && (
                            <button
                              onClick={() => deleteMedia(index)}
                              className="text-red-500"
                            >
                              <RiDeleteBin6Line />
                            </button>
                          )}
                        </div>

                        {/* INPUTS */}
                        <div className="grid gap-3 md:grid-cols-[minmax(160px,0.75fr)_minmax(220px,1.5fr)_120px_110px_auto]">
                          <input
                            placeholder="File Name"
                            value={m.fileName || ""}
                            disabled={!isEditing}
                            onChange={(e) => {
                              const value = e.target.value;
                              setMainData((prev) => ({
                                ...prev,
                                media: prev.media.map((v, i) =>
                                  i === index ? { ...v, fileName: value } : v,
                                ),
                              }));
                            }}
                            className="min-h-11 min-w-0 rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold outline-none focus:border-sky-400 focus:ring-4 focus:ring-sky-500/15"
                          />

                          <input
                            placeholder="File URL"
                            value={m.link || ""}
                            disabled={!isEditing}
                            onChange={(e) => {
                              const value = e.target.value;
                              setMainData((prev) => ({
                                ...prev,
                                media: prev.media.map((v, i) =>
                                  i === index ? { ...v, link: value } : v,
                                ),
                              }));
                            }}
                            className="min-h-11 min-w-0 rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold outline-none focus:border-sky-400 focus:ring-4 focus:ring-sky-500/15"
                          />

                          {(m.type === "image" || m.type === "gif") && (
                            <input
                              placeholder="Duration (s)"
                              value={m.duration || ""}
                              disabled={!isEditing}
                              onChange={(e) => {
                                const value = e.target.value;
                                setMainData((prev) => ({
                                  ...prev,
                                  media: prev.media.map((v, i) =>
                                    i === index ? { ...v, duration: value } : v,
                                  ),
                                }));
                              }}
                              className="min-h-11 min-w-0 rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold outline-none focus:border-sky-400 focus:ring-4 focus:ring-sky-500/15"
                            />
                          )}
                          {m.type === "video" && <span className="hidden md:block" />}

                          <input
                            placeholder="Size"
                            value={m.size || ""}
                            disabled={!isEditing}
                            onChange={(e) => {
                              const value = e.target.value;
                              setMainData((prev) => ({
                                ...prev,
                                media: prev.media.map((v, i) =>
                                  i === index ? { ...v, size: value } : v,
                                ),
                              }));
                            }}
                            className="min-h-11 min-w-0 rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold outline-none focus:border-sky-400 focus:ring-4 focus:ring-sky-500/15"
                          />

                          {/* UPLOAD */}
                          <label
                            className={`inline-flex min-h-11 items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-black ${
                              isEditing
                                ? "cursor-pointer bg-sky-600 text-white hover:bg-sky-700"
                                : "bg-gray-300 text-white"
                            }`}
                          >
                            <UploadCloud size={16} />
                            Upload
                            <input
                              type="file"
                              hidden
                              disabled={!isEditing}
                              accept={
                                m.type === "video"
                                  ? "video/*"
                                  : m.type === "gif"
                                    ? "image/gif"
                                    : "image/png,image/jpeg"
                              }
                              onChange={(e) =>
                                handleFileUpload(e, index, m.type)
                              }
                            />
                          </label>
                        </div>

                        {/* ASPECT RATIO + EXPIRY ROW */}
                        <div className="mt-3 grid gap-3 sm:grid-cols-[1fr_2fr]">
                          {/* Aspect Ratio */}
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-black uppercase text-slate-400 whitespace-nowrap">Ratio</span>
                            <input
                              placeholder="e.g. 16:9"
                              value={m.aspectRatio || ""}
                              disabled={!isEditing}
                              onChange={(e) => {
                                const value = e.target.value;
                                setMainData((prev) => ({
                                  ...prev,
                                  media: prev.media.map((v, i) =>
                                    i === index ? { ...v, aspectRatio: value } : v,
                                  ),
                                }));
                              }}
                              className="min-h-9 w-full min-w-0 rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-semibold outline-none focus:border-sky-400 focus:ring-4 focus:ring-sky-500/15"
                            />
                            {m.aspectRatio && (
                              <span className="shrink-0 rounded-full bg-sky-100 px-2 py-0.5 text-xs font-black text-sky-700">
                                {m.aspectRatio}
                              </span>
                            )}
                          </div>

                          {/* Expiry (datetime-local) — stored in local time, sent with tz offset on submit */}
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-black uppercase text-slate-400 whitespace-nowrap">Expiry</span>
                            <input
                              type="datetime-local"
                              value={m.expiry || ""}
                              disabled={!isEditing}
                              onChange={(e) => {
                                const value = e.target.value;
                                setMainData((prev) => ({
                                  ...prev,
                                  media: prev.media.map((v, i) =>
                                    i === index ? { ...v, expiry: value } : v,
                                  ),
                                }));
                              }}
                              className="min-h-9 w-full min-w-0 rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-semibold outline-none focus:border-sky-400 focus:ring-4 focus:ring-sky-500/15"
                            />
                          </div>
                        </div>

                        {/* CAMPAIGN SELECTOR ROW */}
                        <div className="mt-3 flex flex-wrap items-center gap-3">
                          <span className="text-xs font-black uppercase text-slate-400 whitespace-nowrap">Campaign</span>
                          <select
                            value={m.campaignId || ""}
                            disabled={!isEditing}
                            onChange={(e) => {
                              const camp = mainData.campaigns.find(
                                (c) => c.campaign_id === e.target.value,
                              );
                              setMainData((prev) => ({
                                ...prev,
                                media: prev.media.map((v, i) =>
                                  i === index
                                    ? {
                                        ...v,
                                        campaignId: camp?.campaign_id || "",
                                        campaignName: camp?.campaign_name || "",
                                        advertiserId: camp?.advertiser_id || "",
                                        advertiserName: camp?.advertiser_name || "",
                                      }
                                    : v,
                                ),
                              }));
                            }}
                            className="min-h-9 min-w-0 flex-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-semibold text-slate-900 outline-none focus:border-sky-400 focus:ring-4 focus:ring-sky-500/15"
                          >
                            <option value="">— Select Campaign —</option>
                            {(mainData.campaigns || []).map((c) => (
                              <option key={c.campaign_id} value={c.campaign_id}>
                                {c.campaign_name} ({c.advertiser_name})
                              </option>
                            ))}
                          </select>
                          {m.advertiserName && (
                            <span className="shrink-0 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-black text-amber-800">
                              {m.advertiserName}
                            </span>
                          )}
                        </div>

                        {/* UPLOAD STATUS */}
                        {(m.uploadStatus || m.progress > 0) && (
                          <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
                            <div className="mb-2 flex items-center justify-between gap-3">
                              <span
                                className={`text-xs font-black uppercase ${
                                  m.uploadStatus === "error"
                                    ? "text-red-600"
                                    : m.uploadStatus === "success"
                                      ? "text-emerald-700"
                                      : "text-sky-700"
                                }`}
                              >
                                {m.uploadMessage || "Uploading..."}
                              </span>
                              <span className="text-xs font-black text-slate-700">
                                {m.progress || 0}%
                              </span>
                            </div>
                            <div className="h-2 overflow-hidden rounded bg-gray-200">
                              <div
                                className={`h-2 rounded transition-all ${
                                  m.uploadStatus === "error"
                                    ? "bg-red-500"
                                    : m.uploadStatus === "success"
                                      ? "bg-emerald-500"
                                      : "bg-cyan-400"
                                }`}
                                style={{ width: `${m.progress || 0}%` }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </Draggable>
                ))}

                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>
      {/* Regular Playlist Sequence */}
      <SequenceEditor
        sequence={mainData.sequence}
        media={mainData.media}
        isEditing={isEditing}
        onSequenceChange={(newSeq) =>
          setMainData((prev) => ({ ...prev, sequence: newSeq }))
        }
        title="Playback Sequence"
      />
      <p className="mt-6 inline-flex w-full items-center justify-end gap-2 text-right text-xs font-bold text-cyan-100/80">
        <Clock3 size={14} />
        Last Update: {mainData.lastUpdate || "Not updated yet"}
      </p>
    </div>
  );
};

export default ChargerUiUpdate;
